import { Component, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideCircleCheck, lucideCircleAlert } from '@ng-icons/lucide';

import { environment } from '../../../environments/environment';
import { UI } from '../ui';
import { AuthService, type CurrentUser } from '../../services/auth.service';

/**
 * `required`: temporary password sent by email (asks for it); `recovery`: signed in through a reset code
 * (no current password); `voluntary`: normal change from Settings.
 */
export type ChangePasswordMode = 'required' | 'recovery' | 'voluntary';

export const MIN_PASSWORD_LENGTH = 12;

@Component({
  selector: 'app-change-password-form',
  standalone: true,
  imports: [FormsModule, NgIcon, ...UI],
  templateUrl: './change-password-form.component.html',
  viewProviders: [
    provideIcons({
      lucideCircleCheck,
      lucideCircleAlert,
    }),
  ],
})
export class ChangePasswordFormComponent {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);

  readonly mode = input<ChangePasswordMode>('voluntary');
  readonly passwordChanged = output<CurrentUser>();

  readonly loading = signal(false);
  readonly success = signal<string | null>(null);
  readonly error = signal<string | null>(null);

  currentPassword = '';
  newPassword = '';
  confirmPassword = '';

  onSubmit(): void {
    this.success.set(null);
    this.error.set(null);

    const needsCurrent = this.mode() !== 'recovery';
    if ((needsCurrent && !this.currentPassword) || !this.newPassword || !this.confirmPassword) {
      this.error.set('All fields are required.');
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.error.set('New passwords do not match.');
      return;
    }

    if (this.newPassword.length < MIN_PASSWORD_LENGTH) {
      this.error.set(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    this.loading.set(true);

    const url = `${environment.apiUrl}/auth/users/set_password/`;
    this.http
      .post<CurrentUser>(
        url,
        {
          ...(needsCurrent ? { current_password: this.currentPassword } : {}),
          new_password: this.newPassword,
          new_password_confirm: this.confirmPassword,
        },
        { withCredentials: true },
      )
      .subscribe({
        next: (user) => {
          this.loading.set(false);
          this.auth.setUser(user);
          this.currentPassword = '';
          this.newPassword = '';
          this.confirmPassword = '';
          if (this.mode() === 'voluntary') {
            this.success.set('Password changed successfully.');
          }
          this.passwordChanged.emit(user);
        },
        error: (err) => {
          this.loading.set(false);
          const newPasswordError = err?.error?.new_password;
          const confirmError = err?.error?.new_password_confirm;
          const message = Array.isArray(newPasswordError)
            ? newPasswordError[0]
            : Array.isArray(confirmError)
              ? confirmError[0]
              : err?.error?.detail ?? err?.error?.current_password?.[0] ?? 'Failed to change password.';
          this.error.set(message);
        },
      });
  }
}
